import React, { useState, useMemo } from 'react';
import { Check, BookOpen, Play, Shuffle, ChevronRight, X, RotateCcw } from 'lucide-react';
import { lessons, getAllExercises, getRandomExercises } from './data/lessons';
import { useProgress } from './hooks/useProgress';

// Brand colors
const colors = {
  primary: '#ff6980',
  primaryLight: '#ff8a9d',
  primaryDark: '#e55a70',
  secondary: '#78ccf4',
  secondaryDark: '#000568',
  accent: '#ffff00',
};

const JapaneseGrammarApp = () => {
  const [practiceMode, setPracticeMode] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [questionCount, setQuestionCount] = useState(10);
  const [currentExercises, setCurrentExercises] = useState([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [sessionComplete, setSessionComplete] = useState(false);

  const { completedLessons, completeLesson, isLessonCompleted, getCompletionPercentage } = useProgress();
  const allExercises = useMemo(() => getAllExercises(), []);

  const startPractice = (mode, lesson = null) => {
    let exercisePool;
    if (mode === 'lesson' && lesson) {
      exercisePool = lesson.exercises.map((ex) => ({
        ...ex,
        lessonId: lesson.id,
        lessonTitle: lesson.title,
        grammarPoints: lesson.grammar
      }));
    } else {
      exercisePool = allExercises;
    }
    const selectedExercises = getRandomExercises(exercisePool, questionCount);
    setPracticeMode(mode);
    setSelectedLesson(lesson);
    setCurrentExercises(selectedExercises);
    setCurrentExerciseIndex(0);
    setUserAnswers({});
    setShowFeedback(false);
    setScore(0);
    setSessionComplete(false);
  };

  const handleAnswer = (answer) => {
    setUserAnswers(prev => ({ ...prev, [currentExerciseIndex]: answer }));
  };

  const checkAnswer = () => {
    const exercise = currentExercises[currentExerciseIndex];
    const userAnswer = userAnswers[currentExerciseIndex];
    let isCorrect = false;
    if (exercise.type === 'fill') {
      isCorrect = userAnswer === exercise.answer;
    } else if (exercise.type === 'build') {
      isCorrect = JSON.stringify(userAnswer) === JSON.stringify(exercise.answer);
    }
    if (isCorrect) setScore(prev => prev + 1);
    setShowFeedback(true);
  };

  const nextExercise = () => {
    if (currentExerciseIndex < currentExercises.length - 1) {
      setCurrentExerciseIndex(prev => prev + 1);
      setShowFeedback(false);
    } else {
      if (practiceMode === 'lesson' && selectedLesson) {
        completeLesson(selectedLesson.id);
      }
      setSessionComplete(true);
    }
  };

  const endSession = () => {
    setPracticeMode(null);
    setSelectedLesson(null);
    setCurrentExercises([]);
    setSessionComplete(false);
  };

  // ============ EXERCISE COMPONENTS ============

  const FillInBlankExercise = ({ exercise }) => {
    const parts = exercise.japanese.split('___');
    const currentAnswer = userAnswers[currentExerciseIndex] || '';

    return (
      <div>
        <p style={{ color: '#525252', fontSize: '18px', marginBottom: '24px' }}>
          {exercise.prompt}
        </p>
        <div style={{
          fontSize: '28px',
          lineHeight: '1.6',
          color: '#262626',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>{parts[0]}</span>
          <select
            value={currentAnswer}
            onChange={(e) => handleAnswer(e.target.value)}
            disabled={showFeedback}
            style={{
              padding: '8px 16px',
              fontSize: '24px',
              border: `2px solid ${showFeedback ? '#d4d4d4' : colors.primary}`,
              borderRadius: '8px',
              backgroundColor: 'white',
              outline: 'none',
              cursor: showFeedback ? 'not-allowed' : 'pointer'
            }}
          >
            <option value="">選択...</option>
            {exercise.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <span>{parts[1]}</span>
        </div>
      </div>
    );
  };

  const SentenceBuildExercise = ({ exercise }) => {
    const selectedWords = userAnswers[currentExerciseIndex] || [];
    const availableWords = exercise.words.filter(word => !selectedWords.includes(word));

    const selectWord = (word) => {
      if (showFeedback) return;
      handleAnswer([...selectedWords, word]);
    };

    const removeWord = (index) => {
      if (showFeedback) return;
      handleAnswer(selectedWords.filter((_, i) => i !== index));
    };

    return (
      <div>
        <p style={{ color: '#525252', fontSize: '18px', marginBottom: '24px' }}>
          {exercise.prompt}
        </p>

        {/* Selected words area */}
        <div style={{
          minHeight: '80px',
          padding: '16px',
          backgroundColor: '#fff5f6',
          border: `2px dashed ${colors.primary}40`,
          borderRadius: '12px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          {selectedWords.length === 0 ? (
            <span style={{ color: '#a3a3a3' }}>単語を選んでください...</span>
          ) : (
            selectedWords.map((word, index) => (
              <button
                key={index}
                onClick={() => removeWord(index)}
                disabled={showFeedback}
                style={{
                  padding: '8px 16px',
                  backgroundColor: colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '18px',
                  cursor: showFeedback ? 'not-allowed' : 'pointer',
                  opacity: showFeedback ? 0.5 : 1
                }}
              >
                {word}
              </button>
            ))
          )}
        </div>

        {/* Available words */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {availableWords.map((word, index) => (
            <button
              key={index}
              onClick={() => selectWord(word)}
              disabled={showFeedback}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                border: '2px solid #d4d4d4',
                borderRadius: '8px',
                fontSize: '18px',
                cursor: showFeedback ? 'not-allowed' : 'pointer',
                opacity: showFeedback ? 0.5 : 1
              }}
            >
              {word}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // ============ SESSION COMPLETE VIEW ============

  if (sessionComplete) {
    const percentage = Math.round((score / currentExercises.length) * 100);

    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#fafafa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '48px',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
          border: '1px solid #e5e5e5'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: `${colors.primary}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Check size={40} color={colors.primary} />
          </div>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#262626', marginBottom: '8px' }}>
            Session Complete
          </h2>
          <p style={{ color: '#737373', marginBottom: '24px' }}>
            {practiceMode === 'lesson' && selectedLesson ? selectedLesson.title : 'Mixed Practice'}
          </p>

          <div style={{ fontSize: '48px', fontWeight: 'bold', color: colors.secondaryDark, marginBottom: '8px' }}>
            {score}/{currentExercises.length}
          </div>
          <p style={{ color: '#737373', marginBottom: '32px' }}>{percentage}% correct</p>

          <button
            onClick={() => startPractice(practiceMode, selectedLesson)}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '12px'
            }}
          >
            <RotateCcw size={20} /> Practice Again
          </button>

          <button
            onClick={endSession}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#f5f5f5',
              color: '#404040',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Back to Lessons
          </button>
        </div>
      </div>
    );
  }

  // ============ EXERCISE VIEW ============

  if (currentExercises.length > 0) {
    const exercise = currentExercises[currentExerciseIndex];
    const isCorrect = showFeedback && (
      exercise.type === 'fill'
        ? userAnswers[currentExerciseIndex] === exercise.answer
        : JSON.stringify(userAnswers[currentExerciseIndex]) === JSON.stringify(exercise.answer)
    );

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '24px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
            <div>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#262626', marginBottom: '4px' }}>
                {practiceMode === 'lesson' && selectedLesson ? selectedLesson.title : 'Mixed Practice'}
              </h2>
              <p style={{ fontSize: '14px', color: '#737373' }}>
                Question {currentExerciseIndex + 1} of {currentExercises.length}
              </p>
            </div>
            <button
              onClick={endSession}
              style={{
                padding: '8px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#a3a3a3'
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Progress bar */}
          <div style={{
            height: '8px',
            backgroundColor: '#e5e5e5',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '32px'
          }}>
            <div style={{
              height: '100%',
              width: `${((currentExerciseIndex + 1) / currentExercises.length) * 100}%`,
              backgroundColor: colors.primary,
              transition: 'width 0.3s ease'
            }} />
          </div>

          {/* Exercise card */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            border: '1px solid #e5e5e5',
            marginBottom: '24px'
          }}>
            {exercise.type === 'fill' ? (
              <FillInBlankExercise exercise={exercise} />
            ) : (
              <SentenceBuildExercise exercise={exercise} />
            )}

            {/* Hint */}
            {!showFeedback && exercise.hint && (
              <p style={{ marginTop: '24px', fontSize: '14px', color: '#a3a3a3', fontStyle: 'italic' }}>
                Hint: {exercise.hint}
              </p>
            )}

            {/* Feedback */}
            {showFeedback && (
              <div style={{
                marginTop: '24px',
                padding: '16px',
                borderRadius: '12px',
                backgroundColor: isCorrect ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${isCorrect ? '#bbf7d0' : '#fecaca'}`
              }}>
                <p style={{ fontWeight: '600', color: isCorrect ? '#15803d' : '#dc2626', marginBottom: '4px' }}>
                  {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
                </p>
                {!isCorrect && (
                  <p style={{ fontSize: '14px', color: '#dc2626' }}>
                    Correct answer: <strong>{exercise.type === 'build' ? exercise.answer.join(' ') : exercise.answer}</strong>
                  </p>
                )}
                {exercise.explanation && (
                  <p style={{ fontSize: '14px', color: '#525252', marginTop: '8px' }}>
                    {exercise.explanation}
                  </p>
                )}
              </div>
            )}

            {/* Action button */}
            <div style={{ marginTop: '24px' }}>
              {!showFeedback ? (
                <button
                  onClick={checkAnswer}
                  disabled={!userAnswers[currentExerciseIndex] || (exercise.type === 'build' && userAnswers[currentExerciseIndex]?.length !== exercise.words.length)}
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: colors.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    opacity: (!userAnswers[currentExerciseIndex] || (exercise.type === 'build' && userAnswers[currentExerciseIndex]?.length !== exercise.words.length)) ? 0.4 : 1
                  }}
                >
                  Check Answer
                </button>
              ) : (
                <button
                  onClick={nextExercise}
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: colors.secondary,
                    color: '#262626',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {currentExerciseIndex < currentExercises.length - 1 ? 'Next Question' : 'Complete Session'}
                  <ChevronRight size={20} />
                </button>
              )}
            </div>
          </div>

          {/* Grammar reference */}
          {exercise.grammarPoints && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              border: '1px solid #e5e5e5'
            }}>
              <h3 style={{ fontWeight: '600', color: '#262626', marginBottom: '12px' }}>Grammar Reference</h3>
              {exercise.grammarPoints.map((point, index) => (
                <div key={index} style={{ fontSize: '14px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '500', color: colors.primary }}>{point.point}</span>
                  <span style={{ color: '#737373' }}> — {point.explanation}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============ MAIN LESSON LIST VIEW ============

  const completionPercent = getCompletionPercentage(lessons.length);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '32px 24px' }}>

        {/* Header */}
        <header style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: colors.secondaryDark,
            marginBottom: '8px'
          }}>
            Japanese Grammar
          </h1>
          <p style={{ color: '#737373', fontSize: '16px' }}>
            Master grammar with Genki I & II curriculum
          </p>
        </header>

        {/* Quick Practice Card */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '32px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: '1px solid #e5e5e5'
        }}>
          <h2 style={{ fontWeight: '600', color: '#262626', marginBottom: '16px', fontSize: '18px' }}>
            Quick Practice
          </h2>

          {/* Question count */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#737373', marginBottom: '8px' }}>
              <span>Questions per session</span>
              <span style={{ fontWeight: '500', color: '#262626' }}>{questionCount}</span>
            </div>
            <input
              type="range"
              min="5"
              max="20"
              value={questionCount}
              onChange={(e) => setQuestionCount(parseInt(e.target.value))}
              style={{
                width: '100%',
                height: '8px',
                accentColor: colors.primary,
                cursor: 'pointer'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#a3a3a3', marginTop: '4px' }}>
              <span>5</span>
              <span>20</span>
            </div>
          </div>

          <button
            onClick={() => startPractice('all')}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}
          >
            <Shuffle size={20} />
            Practice All Grammar
          </button>
        </section>

        {/* Progress Card */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '32px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: '1px solid #e5e5e5'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ fontWeight: '600', color: '#262626', fontSize: '18px' }}>Your Progress</h2>
            <span style={{ fontSize: '14px', color: '#737373' }}>
              {completedLessons.length} of {lessons.length} lessons
            </span>
          </div>
          <div style={{
            height: '12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${completionPercent}%`,
              backgroundColor: colors.secondary,
              transition: 'width 0.5s ease'
            }} />
          </div>
          <p style={{ textAlign: 'right', fontSize: '12px', color: '#a3a3a3', marginTop: '8px' }}>
            {completionPercent}% complete
          </p>
        </section>

        {/* Lessons by Book */}
        {['Genki I', 'Genki II'].map(book => (
          <section key={book} style={{ marginBottom: '40px' }}>
            {/* Book Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: `3px solid ${colors.primary}`
            }}>
              <BookOpen size={22} color={colors.primary} />
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: colors.secondaryDark,
                margin: 0
              }}>
                {book}
              </h2>
            </div>

            {/* Lessons List - Single Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {lessons.filter(l => l.book === book).map(lesson => {
                const completed = isLessonCompleted(lesson.id);

                return (
                  <button
                    key={lesson.id}
                    onClick={() => startPractice('lesson', lesson)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      backgroundColor: completed ? '#f0fdf4' : 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      border: completed ? '2px solid #86efac' : '1px solid #e5e5e5',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!completed) {
                        e.currentTarget.style.borderColor = colors.primary;
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(255,105,128,0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!completed) {
                        e.currentTarget.style.borderColor = '#e5e5e5';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        {/* Lesson title row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          {completed ? (
                            <div style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              backgroundColor: '#22c55e',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Check size={14} color="white" />
                            </div>
                          ) : (
                            <div style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              backgroundColor: `${colors.primary}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Play size={12} color={colors.primary} />
                            </div>
                          )}
                          <span style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#262626'
                          }}>
                            Lesson {lesson.id}: {lesson.title}
                          </span>
                        </div>

                        {/* Grammar tags */}
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                          marginLeft: '34px'
                        }}>
                          {lesson.grammar.map((point, idx) => (
                            <span
                              key={idx}
                              style={{
                                padding: '4px 10px',
                                backgroundColor: `${colors.secondary}25`,
                                color: colors.secondaryDark,
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}
                            >
                              {point.point}
                            </span>
                          ))}
                        </div>
                      </div>

                      <ChevronRight size={20} color="#d4d4d4" style={{ flexShrink: 0, marginLeft: '12px' }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ))}

        {/* Footer */}
        <footer style={{ textAlign: 'center', padding: '24px 0', color: '#a3a3a3', fontSize: '14px' }}>
          <p>Progress is saved automatically in your browser.</p>
          <p style={{ marginTop: '4px' }}>Based on Genki I & II textbooks</p>
          <p style={{ marginTop: '16px' }}>
            <a
              href="https://everycitywhispers.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: colors.primary, textDecoration: 'none' }}
            >
              everycitywhispers.com
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default JapaneseGrammarApp;
